(() => {
  const esc = (value) => String(value == null ? '' : value).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
  const arrow = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 12h14M14 7l5 5-5 5"></path></svg>';

  function init(root) {
    if (!root || root.dataset.abmPlus) return;
    root.dataset.abmPlus = 'true';

    /* Compact header on scroll (Horizon scrolls .page-wrapper on desktop). */
    let ticking = false;
    const scrollY = () => Math.max(window.scrollY || 0, (document.querySelector('.page-wrapper') || {}).scrollTop || 0);
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        root.classList.toggle('is-scrolled', scrollY() > 24);
        ticking = false;
      });
    };
    document.addEventListener('scroll', onScroll, { capture: true, passive: true });
    onScroll();

    /* Mobile drawer sits flush under the header bar. */
    const bar = root.querySelector('.abm-header');
    const setDrawerTop = () => bar && root.style.setProperty('--abm-drawer-top', `${Math.round(bar.getBoundingClientRect().bottom)}px`);
    root.querySelector('[data-abm-menu-toggle]')?.addEventListener('click', setDrawerTop);
    window.addEventListener('resize', () => root.classList.contains('menu-open') && setDrawerTop(), { passive: true });

    /* Mega menu hover intent on pointer devices. */
    const canHover = window.matchMedia('(hover: hover) and (min-width: 990px)');
    const items = [...root.querySelectorAll('.abm-nav-item--mega')];
    const setItem = (item, open) => {
      item.classList.toggle('is-open', open);
      const toggle = item.querySelector('.abm-nav-item__toggle');
      toggle && toggle.setAttribute('aria-expanded', String(open));
    };
    items.forEach((item) => {
      let timer;
      item.addEventListener('mouseenter', () => {
        if (!canHover.matches) return;
        clearTimeout(timer);
        timer = setTimeout(() => {
          items.forEach((other) => other !== item && setItem(other, false));
          setItem(item, true);
        }, 90);
      });
      item.addEventListener('mouseleave', () => {
        if (!canHover.matches) return;
        clearTimeout(timer);
        timer = setTimeout(() => setItem(item, false), 180);
      });
    });

    /* Search overlay with predictive results. */
    const search = root.querySelector('[data-abm-search]');
    if (!search) return;
    const input = search.querySelector('[data-abm-search-input]');
    const results = search.querySelector('[data-abm-search-results]');
    const popular = search.querySelector('[data-abm-search-popular]');
    const panel = search.querySelector('.abm-search__panel');
    const labels = results.dataset;
    let lastFocus = null;
    let controller = null;
    let debounce;

    const money = (amount) => {
      const value = Number(amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      const format = search.dataset.moneyFormat || '${{amount}}';
      return format.replace(/\{\{\s*\w+\s*\}\}/, value);
    };

    const open = () => {
      lastFocus = document.activeElement;
      items.forEach((item) => setItem(item, false));
      search.classList.add('is-open');
      search.setAttribute('aria-hidden', 'false');
      document.documentElement.classList.add('abm-search-lock');
      setTimeout(() => input && input.focus({ preventScroll: true }), 60);
    };
    const close = () => {
      if (!search.classList.contains('is-open')) return;
      search.classList.remove('is-open');
      search.setAttribute('aria-hidden', 'true');
      document.documentElement.classList.remove('abm-search-lock');
      lastFocus && lastFocus.focus && lastFocus.focus({ preventScroll: true });
    };

    root.querySelectorAll('[data-abm-search-open]').forEach((trigger) => trigger.addEventListener('click', (event) => {
      if (event.metaKey || event.ctrlKey) return;
      event.preventDefault();
      open();
    }));
    search.querySelectorAll('[data-abm-search-close]').forEach((el) => el.addEventListener('click', close));
    document.addEventListener('keydown', (event) => {
      if (!search.classList.contains('is-open')) return;
      if (event.key === 'Escape') {
        close();
        return;
      }
      if (event.key !== 'Tab') return;
      const focusables = [...panel.querySelectorAll('a[href],button,input:not([type=hidden])')].filter((n) => n.offsetParent !== null);
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });

    const render = (query, data) => {
      const found = (data && data.resources && data.resources.results) || {};
      const products = (found.products || []).slice(0, 4);
      const pages = [...(found.pages || []), ...(found.collections || []), ...(found.articles || [])].slice(0, 6);
      const allUrl = `${search.dataset.searchUrl}?q=${encodeURIComponent(query)}&options%5Bprefix%5D=last`;
      if (!products.length && !pages.length) {
        results.innerHTML = `<p class="abm-search__empty">${esc(labels.labelEmpty)} “${esc(query)}”</p>`;
        return;
      }
      let html = '<div class="abm-search__grid">';
      if (products.length) {
        html += `<div><p class="abm-search__label">${esc(labels.labelProducts)}</p><ul class="abm-search__products" role="list">`;
        products.forEach((p) => {
          const image = p.featured_image && p.featured_image.url ? p.featured_image.url : p.image;
          const src = image ? `${image}${image.includes('?') ? '&' : '?'}width=400` : '';
          html += `<li><a href="${esc(p.url)}"><span class="abm-search__thumb">${src ? `<img src="${esc(src)}" alt="" loading="lazy" width="400" height="400">` : ''}</span>${p.vendor ? `<small>${esc(p.vendor)}</small>` : ''}<strong>${esc(p.title)}</strong><span>${esc(money(p.price))}</span></a></li>`;
        });
        html += '</ul></div>';
      }
      if (pages.length) {
        html += `<div><p class="abm-search__label">${esc(labels.labelPages)}</p><ul class="abm-search__pages" role="list">`;
        pages.forEach((p) => { html += `<li><a href="${esc(p.url)}"><span>${esc(p.title)}</span>${arrow}</a></li>`; });
        html += '</ul></div>';
      }
      html += `</div><a class="abm-search__all" href="${esc(allUrl)}">${esc(labels.labelAll)} ${arrow}</a>`;
      results.innerHTML = html;
    };

    input && input.addEventListener('input', () => {
      const query = input.value.trim();
      clearTimeout(debounce);
      if (query.length < 2) {
        controller && controller.abort();
        results.innerHTML = '';
        popular && (popular.hidden = false);
        return;
      }
      debounce = setTimeout(async () => {
        controller && controller.abort();
        controller = new AbortController();
        results.classList.add('is-loading');
        try {
          const url = `${search.dataset.suggestUrl}.json?q=${encodeURIComponent(query)}&resources%5Btype%5D=product,page,collection,article&resources%5Blimit%5D=8&resources%5Boptions%5D%5Bunavailable_products%5D=last`;
          const data = await fetch(url, { signal: controller.signal }).then((r) => r.json());
          popular && (popular.hidden = true);
          render(query, data);
        } catch (error) {
          if (error.name !== 'AbortError') results.innerHTML = '';
        } finally {
          results.classList.remove('is-loading');
        }
      }, 220);
    });
  }

  document.querySelectorAll('[data-abm-header]').forEach(init);
  document.addEventListener('shopify:section:load', (event) => event.target.querySelectorAll('[data-abm-header]').forEach(init));
})();
